import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, Clock, CheckCircle, AlertCircle, Eye, MessageSquare, FileText } from 'lucide-react';
import { createWordDocument } from '../../utils/documentExport';
import { documentsAPI, projectsAPI } from '../../services/api';
import ClientDraftReview from '../../components/documents/ClientDraftReview';
import ClientCollaboratorsModal from '../../components/documents/ClientCollaboratorsModal';
import ClientWorkflowSetupModal from '../../components/documents/ClientWorkflowSetupModal';
import { useDraftCollaboration } from '../../hooks/useDraftCollaboration';
import { VisibilityBadge } from '../../components/documents/CommentVisibility';

import BackButton from '../../components/common/BackButton';
// Fetch single document for review
const fetchDocument = async (id) => {
  const res = await documentsAPI.getById(id);
  return res.data?.document || res.document || res;
};

export default function DocumentDraftReviewPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showFullComponent, setShowFullComponent] = useState(false);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [preferredMode, setPreferredMode] = useState(null);
  const [pendingMode, setPendingMode] = useState('in_app');
  const [offlineFile, setOfflineFile] = useState(null);
  const [setupCompleteOverride, setSetupCompleteOverride] = useState(false);
  const [ignoreForceSetup, setIgnoreForceSetup] = useState(false);
  const apiBase = import.meta.env.VITE_API_URL || '/api';
  const apiRoot = apiBase.replace(/\/api\/?$/, '');
  const setupKey = `client-doc-setup:${id}`;
  const safeStorageGet = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (err) {
      return null;
    }
  };
  const safeStorageSet = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (err) {
      // ignore storage errors (tracking prevention, etc.)
    }
  };

  const resolveFileUrl = (url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/uploads') || url.startsWith('/api')) {
      return `${apiRoot}${url}`;
    }
    return url;
  };

  // For files stored as .html (legacy), fetch and convert to .doc on download
  const downloadAsWord = async (url, displayName) => {
    const absUrl = resolveFileUrl(url);
    const baseName = displayName.replace(/\.html?$/i, '');
    try {
      const res = await fetch(absUrl);
      const html = await res.text();
      const blob = createWordDocument(html);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${baseName}.doc`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Fallback: direct link
      window.open(absUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleFileDownload = (url, name) => {
    if (/\.html?$/i.test(name)) {
      downloadAsWord(url, name);
    } else {
      const a = document.createElement('a');
      a.href = resolveFileUrl(url);
      a.download = name;
      a.rel = 'noopener';
      a.click();
    }
  };

  const friendlyFileName = (name) => name?.replace(/\.html?$/i, '.doc') || name;

  const { data: document, isLoading, error } = useQuery({
    queryKey: ['document', id],
    queryFn: () => fetchDocument(id),
  });
  const { updateWorkflow } = useDraftCollaboration(id);
  // The document's comment thread; the server removes Stratel's internal notes
  const { data: threadComments = [] } = useQuery({
    queryKey: ['document-comments', id],
    queryFn: () => documentsAPI.getComments(id),
    select: (res) => res.data?.comments || [],
    enabled: !!id,
  });

  const projectId = document?.projectId?._id || document?.projectId;
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsAPI.getById(projectId),
    select: (res) => res.data?.project || res.project || res.data,
    enabled: !!projectId,
  });
  const setupComplete = setupCompleteOverride || safeStorageGet(setupKey) === 'true';
  const hasWorkflow =
    (document?.collaborationWorkflow?.internal?.steps || []).length > 0
    && (document?.collaborationWorkflow?.client?.steps || []).length > 0;
  const hasCollaborators = (project?.clientContacts || []).length > 0;
  const forceSetup = !ignoreForceSetup && searchParams.get('setup') === '1';

  const clearSetupParam = () => {
    if (searchParams.get('setup') !== '1') return;
    navigate(`/documents/draft/${id}`, { replace: true });
  };

  // Issue 4: If server confirms setup is complete, persist to localStorage so other
  // devices/browsers don't redundantly show setup modals again.
  useEffect(() => {
    if (hasCollaborators && hasWorkflow) {
      safeStorageSet(setupKey, 'true');
    }
  }, [hasCollaborators, hasWorkflow]);

  useEffect(() => {
    if (!forceSetup || !document) return;
    if (showCollaboratorsModal || showWorkflowModal) return;
    // Issue 1: if collaborators already configured, skip straight to workflow setup
    if (hasCollaborators) {
      setShowWorkflowModal(true);
    } else {
      setShowCollaboratorsModal(true);
    }
  }, [forceSetup, document, showCollaboratorsModal, showWorkflowModal, hasCollaborators]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-lg shadow p-8 max-w-md w-full">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mx-auto mb-4">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-center text-lg font-medium text-gray-900 mb-2">Error Loading Document</h3>
          <p className="text-center text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  const statusLabels = {
    draft: 'In Preparation',
    internal_review: 'In Preparation',
    shared_with_client: 'Ready for Your Review',
    client_review: 'Under Review',
    awaiting_internal_response: 'Response Sent',
    draft_approved: 'Approved',
    final_version_pending: 'Being Finalized',
    final_version_ready: 'Ready to Sign',
    revision: 'Revision Requested',
    approved: 'Approved',
    denied: 'Returned',
  };

  // Check if document is in a draft workflow (not in final_signature_complete or archived)
  const isDraftWorkflow = !['final_signature_complete', 'archived'].includes(document?.status);

  if (!isDraftWorkflow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-lg shadow p-8 max-w-md w-full">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mx-auto mb-4">
            <CheckCircle className="h-6 w-6 text-blue-600" />
          </div>
          <h3 className="text-center text-lg font-medium text-gray-900 mb-2">Document Complete</h3>
          <p className="text-center text-gray-600 mb-4">
            This document has been finalized and is ready for signature or archival.
          </p>
          <p className="text-center text-sm text-gray-500">
            Status: {statusLabels[document?.status] || document?.status?.replace(/_/g, ' ')}
          </p>
        </div>
      </div>
    );
  }

  if (showFullComponent) {
    return (
      <div>
        <button
          onClick={() => setShowFullComponent(false)}
          className="mb-6 inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
        >
          Back
        </button>
        <ClientDraftReview
          documentId={id}
          document={document}
          preferredMode={preferredMode}
          initialOfflineFile={offlineFile}
          onClose={() => setShowFullComponent(false)}
        />
      </div>
    );
  }

  // Clients receive a filtered iteration list, so match by number rather than array position
  const currentIteration = document?.draftIterations?.find(
    (iter) => iter.iterationNumber === document?.currentDraftIteration,
  )
    || document?.draftIterations?.[document?.draftIterations?.length - 1]
    || null;
  const previewComments = threadComments;
  const revisionRequestCount = previewComments.filter((c) => c.kind === 'revision_request').length;
  const originalIteration = document?.draftSharedWithClientIteration
    ? document?.draftIterations?.find(
      (iter) => iter.iterationNumber === document.draftSharedWithClientIteration
    )
    : document?.draftIterations?.[0] || null;
  const uploadedAt = currentIteration?.uploadedAt
    ? new Date(currentIteration.uploadedAt).toLocaleString()
    : 'Not available';
  const fileName = currentIteration?.fileName || document?.name || 'Current draft';
  const fileUrl = currentIteration?.fileUrl ? resolveFileUrl(currentIteration.fileUrl) : '';
  const sharedNotesAt = document?.draftSharedWithClientAt
    ? new Date(document.draftSharedWithClientAt).toLocaleString()
    : null;
  const sharedNotesBy = document?.draftSharedWithClientBy
    ? `${document.draftSharedWithClientBy.firstName || ''} ${document.draftSharedWithClientBy.lastName || ''}`.trim()
    : '';
  const openReview = (mode) => {
    setPreferredMode(mode);
    setOfflineFile(null);
    setShowFullComponent(true);
  };

  // Issue 1: smart routing — skip whichever setup steps are already done
  const handleStartReviewFlow = () => {
    if (!forceSetup && (setupComplete || (hasCollaborators && hasWorkflow))) {
      openReview('in_app');
      return;
    }
    setPendingMode('in_app');
    // Collaborators already set up — skip straight to workflow routing setup
    if (!forceSetup && hasCollaborators && !hasWorkflow) {
      setShowWorkflowModal(true);
      return;
    }
    setShowCollaboratorsModal(true);
  };

  // Issue 2: "Upload Edited File" — offline path
  const handleStartOfflineFlow = () => {
    if (!forceSetup && (setupComplete || (hasCollaborators && hasWorkflow))) {
      openReview('offline');
      return;
    }
    setPendingMode('offline');
    if (!forceSetup && hasCollaborators && !hasWorkflow) {
      setShowWorkflowModal(true);
      return;
    }
    setShowCollaboratorsModal(true);
  };

  const handleCollaboratorsContinue = () => {
    setShowCollaboratorsModal(false);
    setShowWorkflowModal(true);
  };

  const handleWorkflowSave = async (payload) => {
    await updateWorkflow(payload);
    safeStorageSet(setupKey, 'true');
    setSetupCompleteOverride(true);
    setShowWorkflowModal(false);
    // Go straight into review with whichever mode the client originally chose
    openReview(pendingMode);
    clearSetupParam();
  };

  const handleCancelCollaborators = () => {
    setShowCollaboratorsModal(false);
    setIgnoreForceSetup(true);
    clearSetupParam();
  };

  const handleCancelWorkflow = () => {
    setShowWorkflowModal(false);
    setIgnoreForceSetup(true);
    clearSetupParam();
  };

  const isProjectCompleted = project?.status === 'completed';

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back to wherever the person came from (Signatures, Documents, a project) */}
      <BackButton fallbackTo={projectId ? `/projects/${projectId}/documents` : '/documents'} className="mb-6" />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 break-all">{document?.name}</h1>
        <p className="text-gray-600">{document?.description}</p>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {/* Current status */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Current Status</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {statusLabels[document?.status] || document?.status?.replace(/_/g, ' ')}
              </p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        {/* Approval status */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Your Approval</p>
              <p className="text-lg font-semibold mt-1">
                {document?.approval?.client ? (
                  <span className="text-green-700 flex items-center gap-1">
                    <CheckCircle size={18} />
                    Approved
                  </span>
                ) : (
                  <span className="text-yellow-700 flex items-center gap-1">
                    <Clock size={18} />
                    Pending
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Draft round */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Review Round</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                #{document?.draftApprovalRound || 1}
              </p>
            </div>
            <MessageSquare className="h-8 w-8 text-primary-500" />
          </div>
        </div>
      </div>

      {/* Quick actions — hidden for completed projects */}
      {!isProjectCompleted && (
        <div className="bg-primary-50 rounded-lg border border-primary-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Next Steps</h2>
          <p className="text-gray-700 mb-6">
            {document?.status === 'shared_with_client' || document?.status === 'client_review'
              ? 'Review and annotate the draft in your browser, or edit it in your own tools and upload your changes.'
              : document?.status === 'awaiting_internal_response'
              ? 'The consulting team is reviewing your feedback. Check back soon for updates.'
              : 'Review this document and provide your feedback.'}
          </p>

          {(document?.status === 'shared_with_client' || document?.status === 'client_review') && (
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleStartReviewFlow}
                className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 font-medium"
              >
                <Eye size={18} />
                Review &amp; Edit in App
              </button>
              <button
                onClick={handleStartOfflineFlow}
                className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                <FileText size={16} />
                Upload Edited File
              </button>
            </div>
          )}
        </div>
      )}

      {document?.draftSharedWithClientNotes && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Notes from the team</h2>
          <p className="text-sm text-gray-500 mb-4">
            {sharedNotesAt ? `Sent ${sharedNotesAt}` : 'Sent recently'}
            {sharedNotesBy ? ` by ${sharedNotesBy}` : ''}
          </p>
          <p className="text-gray-800 whitespace-pre-line">
            {document.draftSharedWithClientNotes}
          </p>
        </div>
      )}

      {/* Current version info */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Draft Version</h2>

        {currentIteration && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Version</p>
              <p className="text-base text-gray-900 font-medium">
                Draft {document?.draftApprovalRound || 1} - Iteration {document?.currentDraftIteration}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-2">File</p>
              {fileUrl ? (
                <button
                  type="button"
                  onClick={() => handleFileDownload(fileUrl, fileName)}
                  className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
                >
                  <Download size={16} />
                  {friendlyFileName(fileName)}
                </button>
              ) : (
                <p className="text-sm text-gray-500">No file available</p>
              )}
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-2">Uploaded</p>
              <p className="text-base text-gray-900">
                {uploadedAt}
              </p>
            </div>

            {currentIteration?.uploadedByRole && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Uploaded By</p>
                <p className="text-base text-gray-900">
                  {currentIteration?.uploadedByRole === 'client'
                    ? 'Your Team'
                    : 'Internal Team'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {originalIteration && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText size={18} />
            Original Draft
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-900">{friendlyFileName(originalIteration.fileName)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(originalIteration.uploadedAt).toLocaleString()}
                </p>
              </div>
              {originalIteration.fileUrl && (
                <button
                  type="button"
                  onClick={() => handleFileDownload(originalIteration.fileUrl, originalIteration.fileName)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium rounded-lg transition-colors"
                >
                  <Download size={16} />
                  Download
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comments section preview */}
      {currentIteration && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Comments & Feedback</h2>
          <p className="text-gray-600 mb-4">
            {previewComments.length} comment(s)
            {revisionRequestCount > 0 && (
              <span className="ml-2 text-red-600">({revisionRequestCount} revision request(s))</span>
            )}
          </p>

          {previewComments.slice(0, 3).map((comment, idx) => (
            <div key={comment._id || idx} className="border-t border-gray-200 pt-4 first:border-0 first:pt-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-gray-900">
                    {[comment.userId?.firstName, comment.userId?.lastName].filter(Boolean).join(' ') || (comment.team === 'internal' ? 'Stratel' : 'Your team')}
                  </p>
                  <div className="mt-1"><VisibilityBadge comment={comment} /></div>
                  <p className="text-sm text-gray-600 mt-1">{comment.content}</p>
                </div>
                {comment.kind === 'revision_request' && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-medium">
                    Revision Request
                  </span>
                )}
              </div>
            </div>
          ))}

          {previewComments.length > 3 && (
            <button
              onClick={handleStartReviewFlow}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium mt-4"
            >
              View all comments
            </button>
          )}
        </div>
      )}
      {showCollaboratorsModal && (
        <ClientCollaboratorsModal
          projectId={document?.projectId?._id || document?.projectId}
          documentName={document?.name}
          onClose={handleCancelCollaborators}
          onContinue={handleCollaboratorsContinue}
        />
      )}

      {showWorkflowModal && (
        <ClientWorkflowSetupModal
          orgId={project?.clientId?._id || project?.clientId}
          initialWorkflow={document?.collaborationWorkflow}
          onClose={handleCancelWorkflow}
          onSave={handleWorkflowSave}
        />
      )}
    </div>
  );
}
